import React, { useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import Button from './Button'
import SignupCTA from './SignupCTA'
import HostLink from './HostLink'
import { hasCachedSession } from '../session'

/**
 * Top navigation for the public (logged-out) marketing pages. Rendered by each
 * marketing page (Home/Pricing/About/FAQ/legal) rather than globally, so the
 * authenticated app keeps its own dark NavBar (see App.jsx). If a cached session
 * exists we surface a "Dashboard" link instead of sign-in/trial CTAs — the
 * HttpOnly cookie is still the real authority once they land there.
 *
 * ⚠️ On praenum.com there is never a cached session: the app and its cache live
 * on app.praenum.com (ADR-0062), so the nav always says "Sign in", and /signin
 * sends someone already signed in straight on. The Dashboard branch is for local
 * dev, where one host serves both. Links into the app are HostLinks, which cross
 * to the app host with a real page load.
 */
const LINKS = [
  { to: '/#how-it-works', label: 'Product' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/about', label: 'About' },
  { to: '/faq', label: 'FAQ' },
]

export default function PublicNav() {
  const [open, setOpen] = useState(false)
  const loggedIn = hasCachedSession()

  return (
    <header className="bg-white/90 backdrop-blur border-b border-gray-200 sticky top-0 z-30 print:hidden">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-brand-500 text-white font-bold">
            P
          </span>
          <span className="text-lg font-bold tracking-tight text-brand-900 group-hover:opacity-80">
            Praenum
          </span>
        </Link>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-7 text-sm">
          {LINKS.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-gray-600 hover:text-brand-500 transition-colors ${
                  isActive ? 'text-brand-500 font-medium' : ''
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-3">
          {loggedIn ? (
            <HostLink to="/dashboard">
              <Button variant="primary" size="sm">Go to dashboard</Button>
            </HostLink>
          ) : (
            <>
              <HostLink to="/signin" className="text-sm text-gray-600 hover:text-brand-500 transition-colors">
                Sign in
              </HostLink>
              {/* Swaps to "Private beta — invite only" when signup is closed.
                  In the nav we keep it compact — no mailto sub-line here (the
                  hero/footer CTAs carry the hello@ prompt). */}
              <SignupCTA size="sm" mailto={false} />
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2 -mr-2 text-gray-600"
          onClick={() => setOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {open ? <path d="M6 6l12 12M6 18L18 6" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-gray-200 bg-white px-4 py-3 space-y-1">
          {LINKS.map(l => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block py-2 text-sm text-gray-700 hover:text-brand-500"
            >
              {l.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-gray-100 flex flex-col gap-2">
            {loggedIn ? (
              <HostLink to="/dashboard" onClick={() => setOpen(false)}>
                <Button variant="primary" size="sm" className="w-full">Go to dashboard</Button>
              </HostLink>
            ) : (
              <>
                <HostLink to="/signin" onClick={() => setOpen(false)} className="py-2 text-sm text-gray-700">
                  Sign in
                </HostLink>
                <SignupCTA size="sm" fullWidth mailto={false} />
              </>
            )}
          </div>
        </div>
      )}
    </header>
  )
}
